import { useCallback, useRef, useState } from 'react';

/**
 * Track an element's content-box width, for container-driven layout switches
 * (table ↔ cards, hiding a column, shortening a button label).
 *
 * Use this rather than `useViewportWidth` for anything inside the page body:
 * the sidebar expands and collapses, so a viewport breakpoint says nothing
 * about how much room the content actually has. Viewport width is only right
 * for nav-coupled chrome.
 *
 * ## Why a ref CALLBACK and not `useRef` + an effect (ENG-9722)
 *
 * Every call site here used to be spelled:
 *
 * ```ts
 * const contentRef = useRef<HTMLDivElement>(null);
 * useEffect(() => {
 *   if (!contentRef.current) return;      // <-- silently gives up, forever
 *   const observer = new ResizeObserver(...);
 *   observer.observe(contentRef.current);
 *   return () => observer.disconnect();
 * }, []);                                  // <-- and never runs again
 * ```
 *
 * That works only if the observed element is in the DOM on the very first
 * render. It usually isn't: these pages return a loading or not-found view
 * while the record loads, so the effect ran once with a null ref, bailed, and
 * — with `[]` deps — never re-ran when the real markup finally mounted. The
 * width then sat at its initial fallback (1024) for the life of the page, so
 * every threshold read as "wide": a column that should have been hidden stayed,
 * a table that should have become cards stayed a table, and the flexible column
 * got squeezed to a few dozen pixels with its header painting over the next one.
 *
 * A ref callback has no such window. React invokes it with the node whenever it
 * mounts and with `null` when it unmounts, so the observer attaches the moment
 * the element appears — after a loading gate, after a route transition, after a
 * conditional branch flips — and detaches cleanly.
 *
 * @param fallbackWidth Width reported before the element has been measured.
 *   Only observed for the first frame; keep it wide so a cold render doesn't
 *   flash the narrow layout.
 * @returns `[ref, width]` — spread the ref onto the element to measure.
 */
export function useContainerWidth(fallbackWidth = 1024) {
  const [width, setWidth] = useState(fallbackWidth);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    // Seed synchronously from layout rather than waiting on the observer's
    // first callback, which lands a frame later — long enough to paint the
    // fallback layout once and visibly reflow.
    //
    // `clientWidth` is the padding box, so the padding comes back off to match
    // what `contentRect` reports below. Getting this wrong is worth up to 64px
    // on these pages (`px-4 sm:px-6 lg:px-8`), which straddles the thresholds.
    const style = getComputedStyle(node);
    const seeded =
      node.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    if (seeded > 0) setWidth(seeded);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [ref, width] as const;
}
